import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Section } from "@astryxdesign/core/Section";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Switch } from "@astryxdesign/core/Switch";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Token } from "@astryxdesign/core/Token";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Table, pixel, proportional, type TableColumn } from "@astryxdesign/core/Table";
import { TextInput } from "@astryxdesign/core/TextInput";
import { AlertDialog } from "@astryxdesign/core/AlertDialog";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { MoreMenu } from "@astryxdesign/core/MoreMenu";
import { useToast } from "@astryxdesign/core/Toast";
import { Plus, Pencil, Trash2, Video, HardDrive, ChevronRight, ChevronDown, Search, Unlink, RefreshCw } from "lucide-react";
import {
  type Camera,
  type CreateCameraInput,
  type Device,
  type SyncResult,
  type StreamStatus,
  type UpdateCameraInput,
  api,
} from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { useIsNarrowViewport, useResponsiveTableDensity } from "../../lib/responsive";
import { RetryBanner } from "../../components/RetryBanner";
import { CameraFormDialog } from "./CameraFormDialog";
import { NvrImportDialog } from "./NvrImportDialog";
import { DeviceSyncDialog } from "./DeviceSyncDialog";
import { buildDeviceTree, type DeviceTreeRow } from "./deviceTree";
import type { HealthState } from "../health/useHealthEvents";
import { describeServerHealth } from "../stream/serverHealth";

const LOADING_ROW_COUNT = 4;

// Distinct colors purely so vendors scan apart at a glance in a long list —
// not a status signal, so this deliberately doesn't reuse
// success/warning/error semantics. Typed as a full Record (not a fallback
// default) so adding a vendor without a matching entry here is a compile
// error, not a silently blank column.
const VENDOR_LABEL: Record<Camera["vendor"], string> = { axis: "Axis", hikvision: "Hikvision", generic: "Generic" };
const VENDOR_TOKEN_COLOR: Record<Camera["vendor"], "default" | "teal" | "orange"> = {
  axis: "default",
  hikvision: "teal",
  generic: "orange",
};

// One rendered line: a recorder, or a camera (nested under one or not).
// The table stays flat and the hierarchy is drawn with an indent and a
// disclosure control, rather than reaching for a tree component the design
// system does not have — a recorder is one level deep and never more.
interface TreeRow extends Record<string, unknown> {
  id: string;
  node: DeviceTreeRow;
  health?: StreamStatus;
}

interface CamerasPageProps {
  health: HealthState;
}

export function CamerasPage({ health }: CamerasPageProps) {
  const tableDensity = useResponsiveTableDensity();
  const isNarrow = useIsNarrowViewport();
  const queryClient = useQueryClient();
  const showToast = useToast();

  const {
    data: cameras,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({ queryKey: ["cameras"], queryFn: api.cameras.list });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list });
  const { data: devices } = useQuery({ queryKey: ["devices"], queryFn: api.devices.list });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Camera | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Camera | null>(null);

  const [importOpen, setImportOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState<Device | null>(null);
  const [deviceDeleteTarget, setDeviceDeleteTarget] = useState<Device | null>(null);
  const [detachTarget, setDetachTarget] = useState<Device | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncedDevice, setSyncedDevice] = useState<Device | null>(null);

  // Recorders start expanded. A collapsed-by-default tree hides every camera
  // behind a click on first load, which is the opposite of what this page is
  // for — the flat list at least showed you the cameras.
  const [collapsedDeviceIds, setCollapsedDeviceIds] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState("");

  // Both lists move together: importing changes cameras, deleting a recorder
  // changes devices, and detaching changes both.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["cameras"] });
    queryClient.invalidateQueries({ queryKey: ["devices"] });
  };

  const createMutation = useMutation({
    mutationFn: (input: CreateCameraInput) => api.cameras.create(input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Camera added" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCameraInput }) => api.cameras.update(id, input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Camera updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.cameras.delete(id),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Camera removed" });
      setDeleteTarget(null);
    },
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't delete camera", type: "error" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ camera, enabled }: { camera: Camera; enabled: boolean }) =>
      enabled ? api.cameras.enable(camera.id, camera.version) : api.cameras.disable(camera.id, camera.version),
    onSuccess: invalidate,
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't change camera state", type: "error" });
    },
  });

  const projectNameById = new Map((projects ?? []).map((p) => [p.id, p.name]));

  const expandedDeviceIds = useMemo(
    () => new Set((devices ?? []).map((d) => d.id).filter((id) => !collapsedDeviceIds.has(id))),
    [devices, collapsedDeviceIds],
  );

  // Client-side, over data that's already fully loaded — see ProjectsPage's
  // own comment on why. A camera matches on its own name/location/host OR
  // its recorder's name/host, so searching "control room" or a recorder's
  // IP surfaces every channel under it, not just a device row with a name
  // that happens to match. Filtering happens on the SOURCE arrays, before
  // buildDeviceTree runs, rather than on its output — a camera that matches
  // only through its recorder's name needs that recorder in the filtered
  // device list too, or buildDeviceTree's own device_id lookup would treat
  // it as standalone and misfile it under "no recorder".
  const searchLower = search.trim().toLowerCase();
  const isSearching = searchLower.length > 0;

  const cameraMatches = (c: Camera) =>
    c.name.toLowerCase().includes(searchLower) ||
    c.location.toLowerCase().includes(searchLower) ||
    c.host.toLowerCase().includes(searchLower);
  const deviceMatches = (d: Device) =>
    d.name.toLowerCase().includes(searchLower) || d.host.toLowerCase().includes(searchLower);

  const filteredCameras = useMemo(() => {
    if (!isSearching) return cameras ?? [];
    const deviceById = new Map((devices ?? []).map((d) => [d.id, d]));
    return (cameras ?? []).filter((c) => {
      const device = c.device_id ? deviceById.get(c.device_id) : undefined;
      return cameraMatches(c) || (device ? deviceMatches(device) : false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cameraMatches/deviceMatches close over `search` itself, which is already a dep
  }, [cameras, devices, isSearching, searchLower]);

  const filteredDevices = useMemo(() => {
    if (!isSearching) return devices ?? [];
    return (devices ?? []).filter(
      (d) => deviceMatches(d) || filteredCameras.some((c) => c.device_id === d.id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, filteredCameras, isSearching, searchLower]);

  // A match inside a COLLAPSED recorder must still surface — an operator
  // searching for one camera by name should not also have to remember (or
  // guess) which recorder to expand first. Search overrides the persisted
  // collapse state entirely rather than merging with it, and reverts the
  // moment the search box empties.
  const effectiveExpandedIds = isSearching ? new Set(filteredDevices.map((d) => d.id)) : expandedDeviceIds;

  const rows: TreeRow[] = useMemo(
    () =>
      buildDeviceTree(filteredDevices, filteredCameras, effectiveExpandedIds).map((node) => ({
        id: node.id,
        node,
        health: node.kind === "camera" ? health.byCamera.get(node.camera.id) : undefined,
      })),
    [filteredDevices, filteredCameras, effectiveExpandedIds, health],
  );

  // Counts describe cameras, not rendered rows: a collapsed recorder must not
  // make the page claim there are fewer cameras than there are.
  const cameraCount = (cameras ?? []).length;
  const enabledCount = (cameras ?? []).filter((c) => c.enabled).length;
  const deviceCount = (devices ?? []).length;

  const toggleDevice = (id: string) =>
    setCollapsedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const deleteDeviceMutation = useMutation({
    mutationFn: (id: string) => api.devices.delete(id),
    onSuccess: () => {
      invalidate();
      setDeviceDeleteTarget(null);
      showToast({ body: "Recorder deleted" });
    },
    onError: (err: unknown) => {
      // The refusal when cameras remain arrives here, and its message names
      // how many — far more useful than a generic failure toast.
      setDeviceDeleteTarget(null);
      showToast({ body: err instanceof Error ? err.message : "Couldn't delete the recorder", type: "error" });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (device: Device) => api.devices.sync(device.id),
    onSuccess: (result, device) => {
      invalidate();
      setSyncedDevice(device);
      setSyncResult(result);
    },
    onError: (err: unknown) =>
      showToast({ body: err instanceof Error ? err.message : "Couldn't sync the recorder", type: "error" }),
  });

  const detachMutation = useMutation({
    mutationFn: (id: string) => api.devices.detachCameras(id),
    onSuccess: (result) => {
      invalidate();
      setDetachTarget(null);
      showToast({
        body: `Detached ${result.detached_cameras} camera${result.detached_cameras === 1 ? "" : "s"}`,
      });
    },
    onError: (err: unknown) => {
      setDetachTarget(null);
      showToast({ body: err instanceof Error ? err.message : "Couldn't detach the cameras", type: "error" });
    },
  });

  const openAddDialog = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openImportDialog = (device: Device | null) => {
    setScanTarget(device);
    setImportOpen(true);
  };

  // Columns carrying detail that a phone has no room for. Dropped whole on a
  // narrow viewport rather than squeezed: what they tell you that still
  // matters there (project, vendor, PTZ) is folded into the name cell, and
  // what does not (RTSP address, live viewer count) is a thing you look up on
  // a desktop. Keeping them cost more than their content was worth — they
  // pushed the Enabled switch and the edit/delete buttons past the right edge
  // of a horizontally scrolling table, so the page looked complete on a phone
  // while silently being read-only.
  const detailColumns: TableColumn<TreeRow>[] = [
    {
      key: "project",
      header: "Project",
      width: pixel(140),
      renderCell: (row) => (
        <Text type="supporting" color="secondary">
          {projectNameById.get(projectIdOf(row.node)) ?? "—"}
        </Text>
      ),
    },
    {
      key: "vendor",
      header: "Type",
      width: pixel(150),
      // PTZ rides along in this column rather than getting one of its own: it
      // is another fact about what kind of device this is, and the table is
      // already dense enough that an extra column would cost more than it
      // tells anyone.
      renderCell: (row) =>
        row.node.kind === "device" ? (
          <Token color="teal" label="Recorder" size="sm" />
        ) : (
          <HStack gap={1} vAlign="center" wrap="wrap">
            <Token
              color={VENDOR_TOKEN_COLOR[row.node.camera.vendor]}
              label={VENDOR_LABEL[row.node.camera.vendor]}
              size="sm"
            />
            {row.node.camera.ptz_enabled && <Token color="purple" label="PTZ" size="sm" />}
          </HStack>
        ),
    },
    {
      key: "address",
      header: "Address",
      width: proportional(2),
      renderCell: (row) => (
        <Text type="code" color="secondary">
          {row.node.kind === "device"
            ? `${row.node.device.host}:${row.node.device.http_port || 80}`
            : `${row.node.camera.host}:${row.node.camera.port}${row.node.camera.stream_path}`}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Stream",
      width: pixel(160),
      renderCell: (row) => {
        if (row.node.kind === "device") {
          return (
            <Text type="supporting" color="secondary">
              {row.node.enabledCount}/{row.node.cameras.length} enabled
            </Text>
          );
        }
        if (!row.node.camera.enabled) {
          return (
            <Text type="supporting" color="secondary">
              —
            </Text>
          );
        }
        // describeServerHealth, not the raw row.health.state: an operator
        // reading this table should never see the backend's own enum value
        // ("ready", "connecting"...) printed at them verbatim, and "unknown"
        // (health.phase not live, or no snapshot yet) reads the same as no
        // data at all rather than asserting a fact that might be stale.
        const serverHealth = describeServerHealth(row.health, health.phase === "live");
        if (serverHealth.kind === "unknown") {
          return (
            <Text type="supporting" color="secondary">
              —
            </Text>
          );
        }
        return (
          <Text type="supporting">
            {serverHealth.label}
            {serverHealth.kind === "ready"
              ? ` · ${serverHealth.viewerCount} viewer${serverHealth.viewerCount === 1 ? "" : "s"}`
              : ""}
          </Text>
        );
      },
    },
  ];

  const columns: TableColumn<TreeRow>[] = [
    {
      key: "name",
      // Wider than the other proportional columns because it carries the
      // hierarchy: a recorder row spends width on a disclosure control and an
      // icon, and a channel row on its channel marker, before either gets to
      // the name. At proportional(2) the marker was truncated to "c..".
      header: "Camera",
      width: proportional(3),
      renderCell: (row) => {
        if (row.node.kind === "device") {
          const { device, cameras: children } = row.node;
          const isExpanded = expandedDeviceIds.has(device.id);
          return (
            <HStack gap={1} vAlign="center">
              <IconButton
                label={isExpanded ? `Collapse ${device.name}` : `Expand ${device.name}`}
                icon={isExpanded ? <ChevronDown /> : <ChevronRight />}
                variant="ghost"
                size="sm"
                // Disabled rather than hidden on an empty recorder: the
                // control keeping its place stops every row below it shifting
                // sideways as recorders gain and lose cameras.
                isDisabled={children.length === 0}
                onClick={() => toggleDevice(device.id)}
              />
              <HardDrive size={16} />
              <VStack gap={0}>
                <Text weight="medium">{device.name}</Text>
                <HStack gap={1} vAlign="center" wrap="wrap">
                  <Text type="supporting" color="secondary">
                    {device.model || "Recorder"} · {children.length} camera{children.length === 1 ? "" : "s"}
                  </Text>
                  {isNarrow && (
                    <Text type="supporting" color="secondary">
                      {projectNameById.get(device.project_id) ?? "—"}
                    </Text>
                  )}
                </HStack>
              </VStack>
            </HStack>
          );
        }

        const { camera, isChild } = row.node;
        return (
          // A recorder's cameras are indented so the grouping is visible in a
          // table that is otherwise flat. paddingInlineStart is a spacing-token
          // step rather than a hand-written offset, so the indent tracks the
          // rest of the layout scale.
          //
          // The earlier attempt put a channel marker beside the name instead.
          // As a sibling of the name it was a flex item that shrank to "c.." in
          // whatever width the name left it, which is why the marker now sits
          // on the supporting line and the indent does the grouping.
          <HStack gap={1.5} vAlign="center" paddingInlineStart={isChild ? 5 : 0}>
            <StatusDot
              variant={camera.enabled ? "success" : "neutral"}
              label={camera.enabled ? "Enabled" : "Disabled"}
            />
            <VStack gap={0}>
              <Text weight="medium">{camera.name}</Text>
              {/* The channel marker lives on this line rather than beside the
                  name. As a sibling of the name it was a flex item that shrank
                  to "c.." in the space the name left it; here it shares a line
                  that is already short, and reads as what it is — where this
                  camera sits on the recorder above. */}
              {camera.stream_quality === "sub" && camera.effective_stream_quality === "main" && (
                // The camera is set to the cheap encoding but has no sub
                // stream to fall back on, so it is really being pulled at
                // roughly twenty times the bandwidth. Left unmarked, a wall
                // believed to be running entirely on sub streams could be
                // saturating a site's uplink with nothing anywhere saying so.
                <Token color="orange" label="No sub stream — pulling main" size="sm" />
              )}
              {(isChild || camera.location) && (
                <Text type="supporting" color="secondary">
                  {[isChild && camera.channel_id ? `ch${camera.channel_id}` : null, camera.location]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              )}
              {/* Stands in for the dropped Project and Type columns. A camera
                  list that cannot tell you which site a camera is on is a list
                  of names. */}
              {isNarrow && (
                <HStack gap={1} vAlign="center" wrap="wrap">
                  <Text type="supporting" color="secondary">
                    {projectNameById.get(camera.project_id) ?? "—"}
                  </Text>
                  <Token color={VENDOR_TOKEN_COLOR[camera.vendor]} label={VENDOR_LABEL[camera.vendor]} size="sm" />
                  {camera.ptz_enabled && <Token color="purple" label="PTZ" size="sm" />}
                </HStack>
              )}
            </VStack>
          </HStack>
        );
      },
    },
    ...(isNarrow ? [] : detailColumns),
    {
      key: "enabled",
      header: "Enabled",
      width: pixel(90),
      renderCell: (row) =>
        row.node.kind === "device" ? null : (
          <Switch
            label={`Enable ${row.node.camera.name}`}
            isLabelHidden
            value={row.node.camera.enabled}
            changeAction={async (enabled) => {
              if (row.node.kind !== "camera") return;
              await toggleMutation.mutateAsync({ camera: row.node.camera, enabled });
            }}
          />
        ),
    },
    {
      key: "actions",
      header: "",
      width: pixel(96),
      renderCell: (row) => {
        if (row.node.kind === "device") {
          const { device } = row.node;
          return (
            <HStack gap={1} justify="end">
              <MoreMenu
                label={`Actions for ${device.name}`}
                items={[
                  {
                    // Distinct from "Scan for cameras": that one is for
                    // choosing what to import, this one reconciles what is
                    // already imported with what the recorder says now.
                    label: "Sync from recorder",
                    icon: <RefreshCw />,
                    isDisabled: !device.supports_discovery || syncMutation.isPending,
                    onClick: () => syncMutation.mutate(device),
                  },
                  {
                    label: "Scan for cameras",
                    icon: <Search />,
                    isDisabled: !device.supports_discovery,
                    onClick: () => openImportDialog(device),
                  },
                  { label: "Detach cameras", icon: <Unlink />, onClick: () => setDetachTarget(device) },
                  { type: "divider" },
                  { label: "Delete recorder", icon: <Trash2 />, onClick: () => setDeviceDeleteTarget(device) },
                ]}
              />
            </HStack>
          );
        }

        const { camera } = row.node;
        return (
          <HStack gap={1} justify="end">
            <IconButton
              label={`Edit ${camera.name}`}
              tooltip="Edit"
              icon={<Pencil />}
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(camera);
                setFormOpen(true);
              }}
            />
            <IconButton
              label={`Delete ${camera.name}`}
              tooltip="Delete"
              icon={<Trash2 />}
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(camera)}
            />
          </HStack>
        );
      },
    },
  ];

  let body: ReactNode;
  if (isLoading) {
    body = (
      <VStack gap={2}>
        {Array.from({ length: LOADING_ROW_COUNT }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <Skeleton key={i} height={44} radius={2} index={i} />
        ))}
      </VStack>
    );
  } else if (error) {
    body = (
      <RetryBanner
        title="Couldn't load cameras"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else if (cameraCount === 0 && deviceCount === 0) {
    // Genuine emptiness — checked against the unfiltered counts, not
    // `rows.length`, which is now the FILTERED row count and would fire
    // this same branch for a search with zero matches (a different case,
    // handled below, with a different message and no "add a camera" action
    // that a search box has no business offering).
    body = (
      <EmptyState
        icon={<Video size={32} />}
        title="No cameras yet"
        description="Add a single camera, or add a recorder and import the cameras behind it in one go."
        actions={
          <HStack gap={2}>
            <Button label="Add camera" variant="secondary" icon={<Plus />} onClick={openAddDialog} />
            <Button
              label="Add recorder"
              variant="primary"
              icon={<HardDrive />}
              onClick={() => openImportDialog(null)}
            />
          </HStack>
        }
      />
    );
  } else {
    body = (
      <VStack gap={3}>
        <TextInput
          label="Search cameras"
          isLabelHidden
          placeholder="Search by camera or recorder name, location, or address"
          value={search}
          onChange={setSearch}
          startIcon={<Search size={16} />}
          hasClear
        />
        {rows.length === 0 ? (
          <EmptyState icon={<Search size={32} />} title="No matching cameras" description={`No camera or recorder matches "${search}".`} />
        ) : (
      <Table<TreeRow>
        data={rows}
        idKey={(row) => row.id}
        density={tableDensity}
        hasHover
        dividers="rows"
        columns={columns}
      />
        )}
      </VStack>
    );
  }

  return (
    <Section padding={4}>
      <VStack gap={4}>
        <PageHeader
          title="Cameras"
          description={
            `${cameraCount} camera${cameraCount === 1 ? "" : "s"} configured · ${enabledCount} enabled` +
            (deviceCount > 0 ? ` · ${deviceCount} recorder${deviceCount === 1 ? "" : "s"}` : "")
          }
          actions={
            <HStack gap={2}>
              <Button label="Add camera" variant="secondary" icon={<Plus />} onClick={openAddDialog} />
              <Button
                label="Add recorder"
                variant="primary"
                icon={<HardDrive />}
                onClick={() => openImportDialog(null)}
              />
            </HStack>
          }
        />
        {body}
      </VStack>

      <CameraFormDialog
        isOpen={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <NvrImportDialog
        isOpen={importOpen}
        onOpenChange={setImportOpen}
        existingDevice={scanTarget}
        onImported={(created, skipped) => {
          invalidate();
          showToast({
            body:
              created === 0
                ? "Those channels were already imported"
                : `Imported ${created} camera${created === 1 ? "" : "s"}` +
                  (skipped > 0 ? ` · ${skipped} already existed` : ""),
          });
        }}
      />

      <DeviceSyncDialog
        isOpen={syncResult !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSyncResult(null);
            setSyncedDevice(null);
          }
        }}
        device={syncedDevice}
        result={syncResult}
        onImportNew={() => {
          // Hand straight to the wizard rather than making the operator find
          // the recorder again — it re-scans and shows the same channels with
          // the imported ones already accounted for.
          const device = syncedDevice;
          setSyncResult(null);
          setSyncedDevice(null);
          if (device) openImportDialog(device);
        }}
      />

      <AlertDialog
        isOpen={deviceDeleteTarget !== null}
        onOpenChange={(open) => !open && setDeviceDeleteTarget(null)}
        title={`Delete ${deviceDeleteTarget?.name ?? "this recorder"}?`}
        // The server refuses while cameras remain, so this copy describes the
        // outcome honestly rather than promising a cascade that will not
        // happen — see DeviceUsecase.Delete.
        description="This removes the recorder only. Detach or delete its cameras first; they are not removed with it."
        actionLabel="Delete recorder"
        isActionLoading={deleteDeviceMutation.isPending}
        onAction={() => deviceDeleteTarget && deleteDeviceMutation.mutate(deviceDeleteTarget.id)}
      />

      <AlertDialog
        isOpen={detachTarget !== null}
        onOpenChange={(open) => !open && setDetachTarget(null)}
        title={`Detach cameras from ${detachTarget?.name ?? "this recorder"}?`}
        description="They become ordinary cameras and keep streaming, but they stop being grouped under this recorder and will not pick up its credential changes."
        actionLabel="Detach cameras"
        isActionLoading={detachMutation.isPending}
        onAction={() => detachTarget && detachMutation.mutate(detachTarget.id)}
      />

      <AlertDialog
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.name ?? "this camera"}?`}
        description="This removes the camera and stops any active stream for it. This cannot be undone."
        actionLabel="Delete camera"
        isActionLoading={deleteMutation.isPending}
        onAction={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Section>
  );
}

/** Both kinds of row belong to a project; this reads whichever one it is. */
function projectIdOf(node: DeviceTreeRow): string {
  return node.kind === "device" ? node.device.project_id : node.camera.project_id;
}
